import React, { cloneElement, useEffect, useState } from 'react'
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from 'react-i18next';
import { X } from "lucide-react";
import { cn } from '@/lib/utils';
import { TextButton } from '../common/text-button';
import { IconButton } from '../common/icon-button';

import { useGoogleAuth } from "@/hooks/use-google-auth";
import { GoogleLoginButton } from "./google-login-button";
import { EmailVerifySection } from "./email-verify-section";
import { TermsOfUsePrivacyPolicy } from "./termsofuse-privacypolicy";
import { AgentLogoIcon } from "../agent/assets";
import { agentManager } from '@/manager';
import { ENV } from '@/constant';
import { Image } from '../common/image';
import { seedResolvedAgentFromSSR } from '@/utils/ssr-agent-resolve';
import type { AgentResolveData } from '@/services/agent-resolve';
import { AiAgentImage } from './assets/assets/AiAgentImage';


interface LoginModalProps {
  trigger?: React.ReactElement;
  open?: boolean;
  title?: string;
  onConfirm?: (id: string[]) => void;
  onOpenChange?: (open: boolean) => void;
}

/** 登录弹窗 */
export const LoginModal: React.FC<LoginModalProps> = ({
  trigger,
  open: externalOpen,
  title,
  onConfirm,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const { googleAuth, loading } = useGoogleAuth();
  const getResolvedAgentSnapshot = (): AgentResolveData | null => {
    const data = agentManager.getResolvedAgent();
    return data?.id ? data : null;
  };
  const [agentId, setAgentId] = useState<string>(() => agentManager.getAgentId() || "");
  const [resolvedAgent, setResolvedAgent] = useState<AgentResolveData | null>(() => getResolvedAgentSnapshot());
  const [internalOpen, setInternalOpen] = useState(false);

  useEffect(() => {
    // 仅做本地 seed，不触发 resolveAgentId 接口请求。
    const seeded = seedResolvedAgentFromSSR();
    if (seeded?.id) {
      setAgentId(seeded.id);
      setResolvedAgent({
        id: seeded.id,
        name: seeded.name,
        avatar: seeded.avatar,
        status: seeded.status,
        agentExpiresAt: seeded.agent_expires_at,
      });
    } else {
      setAgentId(agentManager.getAgentId() || "");
      setResolvedAgent(getResolvedAgentSnapshot());
    }

    const offId = agentManager.onAgentIdChange((e) => {
      setAgentId(e.detail.agentId || "");
    });
    const offResolved = agentManager.onResolvedAgentChange((e) => {
      setResolvedAgent(e.detail.resolvedAgent?.id ? e.detail.resolvedAgent : null);
    });

    return () => {
      offId?.();
      offResolved?.();
    };
  }, []);

  // 判断是否使用 trigger 模式（trigger 优先）
  const isTriggerMode = !!trigger;

  // 根据模式决定使用哪个 open 状态
  const currentOpen = isTriggerMode ? internalOpen : externalOpen ?? false;

  // 处理 open 状态变化
  const handleOpenChange = (open: boolean) => {
    if (isTriggerMode) {
      setInternalOpen(open);
    } else {
      onOpenChange?.(open);
    }
  };

  // trigger 点击处理
  const handleTriggerClick = () => {
    handleOpenChange(true);
  };

  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay
        className={cn("fixed inset-0 bg-[rgba(0,0,0,0.2)] backdrop-filter backdrop-blur z-50")}
      />
      <Dialog.Content
        modal-tag-move
        className={cn(
          "flex flex-col",
          "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-[1.25rem] shadow-lg",
          "w-[440px] flex-shrink-0",
          "z-[50] overflow-hidden",
          "w-full md:min-w-[32.25rem] md:max-w-[32.25rem] bg-white rounded-[24px] py-20",
          "[border-image-source:linear-gradient(180deg,_rgba(255,_255,_255,_0.5)_0%,_rgba(255,_255,_255,_0)_13.27%)] [border-image-slice:1] box-border bg-[rgba(255,_255,_255,_1)] [box-shadow:0px_4px_28px_rgba(29,_29,_31,_0.12)]"
        )}
      >
        <div className="flex h-full flex-col items-center justify-center px-10">
          {/* Logo */}
          <div className="flex items-center justify-center mb-10">
            {resolvedAgent?.avatar ? (
              <Image src={resolvedAgent?.avatar} alt="logo" className='size-[48px] rounded-full object-cover' />
            ) : (
              <AiAgentImage className='size-[48px]'/>
            )}
          </div>

          {/* 登录表单 */}
          <div className="w-full max-w-sm">
            <h2 className="text-2xl font-bold mb-8 text-black text-center">
              {t("login.index.welcome_to", { name: resolvedAgent?.name ?? "AI Agent" })}
            </h2>

            <GoogleLoginButton
              loading={loading}
              googleAuth={() => googleAuth(agentId)}
            />

            <div className="flex items-center w-full my-6">
              <div className="flex-1 h-px bg-[rgba(237,237,237,1)]" />
              <span className="mx-4 text-gray-500 text-sm">{t("login.index.or")}</span>
              <div className="flex-1 h-px bg-[rgba(237,237,237,1)]" />
            </div>

            <EmailVerifySection agentId={!ENV.isDevEnv ? agentId : null} />
          </div>

          <TermsOfUsePrivacyPolicy />

        </div>
      </Dialog.Content>
    </Dialog.Portal>
  )

  // trigger 模式
  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleTriggerClick();
              // 保留原有的 onClick 事件
              if (trigger.props.onClick) {
                trigger.props.onClick(e);
              }
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  // open 参数模式
  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
}

